import React from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetLicenceDocument,
  UpdateLicenceStateDocument,
  type GetLicenceQuery,
  type LicenceState,
  type UpdateLicenceStateMutation,
  type UpdateLicenceStateMutationVariables,
} from '@/generated/root-types';
import {
  licenceStateColors,
  translateLicenceState,
  translateAppType,
  translateLicenceType,
  subscriptionStateColors,
  translateSubscriptionState,
} from '@/utils/billingStateLabels';
import { useNotification } from '@/app/contexts/NotificationContext';
import {
  detailBackButtonSx,
  detailHeaderRowWrapSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

const LICENCE_STATES: LicenceState[] = ['ACTIVE', 'EXPIRED', 'SUSPENDED', 'REVOKED'];

function formatAdditionalJsonValue(val: unknown): string {
  if (val == null) return '–';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
    return String(val);
  try {
    return JSON.stringify(val, null, 2);
  } catch {
    return String(val);
  }
}

export default function LicenceDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTenantId = (location.state as { fromTenantId?: string })?.fromTenantId;
  const { showSuccess, showError } = useNotification();

  const subscriptionNavState = React.useMemo(
    () => (id ? { fromLicenceId: id, ...(fromTenantId ? { fromTenantId } : {}) } : undefined),
    [id, fromTenantId],
  );

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';
  const formatMoney = (amount: number, currency: string | null | undefined) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency && currency.length === 3 ? currency : 'EUR',
    }).format(amount);

  const { data, loading, error } = useQuery<GetLicenceQuery>(GetLicenceDocument, {
    variables: { id },
    skip: !id,
  });

  const licence = data?.licence;

  const additionalPropsDisplay = (() => {
    if (!licence?.additionalPropertiesJson?.trim()) return null;
    const raw = licence.additionalPropertiesJson.trim();
    try {
      const v = JSON.parse(raw) as unknown;
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        const entries = Object.entries(v as Record<string, unknown>).sort(([a], [b]) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        return { mode: 'kv' as const, entries };
      }
      return { mode: 'pretty' as const, text: JSON.stringify(v, null, 2) };
    } catch {
      return { mode: 'raw' as const, text: raw };
    }
  })();

  const [draftState, setDraftState] = React.useState<LicenceState | null>(null);
  React.useEffect(() => {
    if (licence) setDraftState(licence.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licence?.id, licence?.state]);

  const [updateLicenceState, { loading: savingStatus }] = useMutation<
    UpdateLicenceStateMutation,
    UpdateLicenceStateMutationVariables
  >(UpdateLicenceStateDocument, {
    refetchQueries: id ? [{ query: GetLicenceDocument, variables: { id } }] : [],
    awaitRefetchQueries: true,
  });

  const handleSaveStatus = async () => {
    if (!id || draftState == null || !licence || draftState === licence.state) return;
    try {
      const res = await updateLicenceState({
        variables: { input: { id, state: draftState } },
      });
      // defaultOptions.mutate uses errorPolicy: 'all' — GraphQL-Fehler können in res.errors stehen, kein throw
      const topErrors = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (topErrors?.length) {
        showError(
          topErrors
            .map((e) => e.message ?? '')
            .filter(Boolean)
            .join(' ') || t('licenceDetail.statusUpdateFailed'),
        );
        return;
      }
      const payload = res.data?.updateLicenceState;
      if (!payload) {
        showError(t('licenceDetail.statusUpdateFailed'));
        return;
      }
      const errs = payload.errors;
      if (errs && errs.length > 0) {
        showError(errs[0]?.message ?? t('licenceDetail.statusUpdateFailed'));
        return;
      }
      showSuccess(t('licenceDetail.statusUpdated'));
    } catch {
      showError(t('licenceDetail.statusUpdateFailed'));
    }
  };

  const handleBack = () => {
    if (fromTenantId) navigate(`/customers/${fromTenantId}`);
    else navigate('/licences');
  };

  if (loading) {
    return (
      <Box
        sx={{
          ...detailPageContainerSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !licence) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>
        <Typography color="error">{error?.message ?? t('errors.licenceNotFound')}</Typography>
      </Box>
    );
  }

  const org = licence.organizationUnit?.organization;
  const sub = licence.subscriptionItem?.subscription;
  const plan = sub?.plan;
  const tenantIdForNav = licence.tenant.id;

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={{ ...detailHeaderRowWrapSx, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={detailBackButtonSx}>
          {t('common.back')}
        </Button>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="h4" component="h1" sx={detailTitleSx}>
            {t('licenceDetail.licence')} {licence.serial}
          </Typography>
        </Box>
        <Chip
          label={translateLicenceState(t, licence.state)}
          color={licenceStateColors[licence.state] || 'default'}
          size="small"
        />
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3, width: '100%' }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate(`/customers/${tenantIdForNav}`)}
        >
          {t('common.toCustomer')}
        </Button>
        {org?.id && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/organizations/${org.id}`)}
          >
            {t('licenceDetail.openOrganization')}
          </Button>
        )}
        {sub?.id && subscriptionNavState && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/subscriptions/${sub.id}`, { state: subscriptionNavState })}
          >
            {t('licenceDetail.openSubscription')}
          </Button>
        )}
      </Stack>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('licenceDetail.assignmentSection')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.tenant')}
            </Typography>
            <Typography variant="body2">{licence.tenant.name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.organization')}
            </Typography>
            <Typography variant="body2">{org?.name ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.organizationUnit')}
            </Typography>
            <Typography variant="body2">{licence.organizationUnit?.name ?? '–'}</Typography>
            {licence.organizationUnit?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {licence.organizationUnit.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.subscriptionPosition')}
            </Typography>
            {licence.subscriptionItem && sub ? (
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
                }}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  gap={1.5}
                  sx={{ mb: plan?.name ? 1 : 0 }}
                >
                  <Link
                    component={RouterLink}
                    to={`/subscriptions/${sub.id}`}
                    state={subscriptionNavState}
                    underline="hover"
                    aria-label={t('licenceDetail.gotoSubscriptionAria', { number: sub.number })}
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: 'primary.main',
                    }}
                  >
                    {t('subscriptions.subNumber')} {sub.number}
                  </Link>
                  <Chip
                    size="small"
                    label={translateSubscriptionState(t, sub.state)}
                    color={subscriptionStateColors[sub.state] || 'default'}
                  />
                </Stack>
                {plan?.name && (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 0.25 }}>
                    {plan.name}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('licenceDetail.noSubscriptionItem')}
              </Typography>
            )}
          </Box>
          {licence.subscriptionItem && (
            <Box
              sx={{
                gridColumn: { xs: '1', sm: '1 / -1' },
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                rowGap: 2,
              }}
            >
              <Box sx={{ minWidth: 140 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('licenceDetail.unitPriceNet')}
                </Typography>
                <Typography variant="body2">
                  {formatMoney(
                    licence.subscriptionItem.unitPriceNet,
                    licence.subscriptionItem.currencyCode,
                  )}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 140 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('licenceDetail.unitPriceGross')}
                </Typography>
                <Typography variant="body2">
                  {formatMoney(
                    licence.subscriptionItem.unitPriceGross,
                    licence.subscriptionItem.currencyCode,
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('licenceDetail.productSection')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.serialNumber')}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {licence.serial}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.app')}
            </Typography>
            <Typography variant="body2">{licence.appName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.appVersion')}
            </Typography>
            <Typography variant="body2">{licence.appVersion}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.appType')}
            </Typography>
            <Typography variant="body2">{translateAppType(t, licence.appType)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.regLevel')}
            </Typography>
            <Typography variant="body2">{licence.appReglevel}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.status')}
            </Typography>
            <Typography variant="body2">{translateLicenceState(t, licence.state)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.licenceType')}
            </Typography>
            <Typography variant="body2">{translateLicenceType(t, licence.type)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.registeredAt')}
            </Typography>
            <Typography variant="body2">{formatDate(licence.registrationDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.validUntil')}
            </Typography>
            <Typography variant="body2">{formatDate(licence.expirationDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.created')}
            </Typography>
            <Typography variant="body2">{formatDate(licence.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.updated')}
            </Typography>
            <Typography variant="body2">{formatDate(licence.updatedAt)}</Typography>
          </Box>
        </Box>
      </Paper>

      {draftState != null && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('licenceDetail.statusSection')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('licenceDetail.statusHint')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="licence-status-label">{t('common.status')}</InputLabel>
              <Select
                labelId="licence-status-label"
                label={t('common.status')}
                value={draftState}
                onChange={(e) => setDraftState(e.target.value as LicenceState)}
              >
                {LICENCE_STATES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {translateLicenceState(t, s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={savingStatus || draftState === licence.state}
              onClick={() => void handleSaveStatus()}
            >
              {t('licenceDetail.saveStatus')}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('licenceDetail.device')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.deviceUidLabel')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.9rem' }}
            >
              {licence.deviceUid ?? '–'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.deviceBrand')}
            </Typography>
            <Typography variant="body2">{licence.deviceBrand ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.deviceModel')}
            </Typography>
            <Typography variant="body2">{licence.deviceModel ?? '–'}</Typography>
          </Box>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.operatingSystem')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {licence.deviceOS ?? '–'}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.deviceInfo')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {licence.deviceInfo ?? '–'}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="caption" color="text.secondary">
              {t('licenceDetail.deviceDetails')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {licence.deviceDetails ?? '–'}
            </Typography>
          </Box>
        </Box>

        {additionalPropsDisplay && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {t('licenceDetail.deviceAdditionalJson')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {t('licenceDetail.deviceAdditionalJsonHint')}
            </Typography>
            {additionalPropsDisplay.mode === 'kv' ? (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
              >
                {additionalPropsDisplay.entries.map(([key, val]) => (
                  <Box key={key}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {key}
                    </Typography>
                    {typeof val === 'object' && val !== null ? (
                      <Typography
                        component="pre"
                        variant="body2"
                        sx={{
                          m: 0,
                          mt: 0.25,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'monospace',
                          fontSize: 12,
                        }}
                      >
                        {formatAdditionalJsonValue(val)}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {formatAdditionalJsonValue(val)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  m: 0,
                  overflow: 'auto',
                  fontSize: 12,
                  bgcolor: 'action.hover',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {additionalPropsDisplay.text}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
